import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { ROUTES } from "../../constants/clientRoutes";

// types
import type { SendMoneyReviewLocationState, SendMoneyWorkflowLocationState } from "../../types/sendMoney";
import type { RootState } from "../../types/redux";

// containers
import SendMoneyWorkflowScreen from "./SendMoneyWorkflowScreen";
import SendMoneyReviewScreen from "./SendMoneyReviewScreen";

const SendMoneyWorkflow = (): React.ReactElement | null => {
  const history = useHistory();
  // Router state is runtime data (refresh / deep link), so every handover key may be missing.
  const { state } = useLocation<Partial<SendMoneyWorkflowLocationState & SendMoneyReviewLocationState> | undefined>();

  const selectedRecipient = useSelector(
    (rootState: RootState) => rootState.sendMoney.selectedRecipient
  );

  const currentQuoteId = useSelector(
    (rootState: RootState) => rootState.sendMoney.quoteId
  );

  // Captured once from the handover push. Step pushes carry no state (pushState
  // structured-clones it), so the handover lives here while the flow is mounted.
  const [workflow] = useState(() => state?.workflow);
  const [review] = useState(() => state?.review);
  const [transactionContextId] = useState(() => state?.transactionContextId);
  const [handoverQuoteId] = useState(() => state?.quoteId);

  // History state survives Back, so browser Forward can resurrect a handover for a superseded
  // quote. The handover carries the quote the client used; it must still be the current one.
  const isHandoverCurrent = handoverQuoteId !== undefined && handoverQuoteId === currentQuoteId;

  const hasWorkflowPages = Boolean(workflow?.pages.length);
  const showWorkflow = hasWorkflowPages && isHandoverCurrent;

  const hasReviewHandover = review !== undefined && transactionContextId !== undefined;
  const showReview = hasReviewHandover && isHandoverCurrent;

  const isExistingRecipient = selectedRecipient !== null;
  const fallbackRoute = isExistingRecipient
    ? ROUTES.SEND_MONEY.TRANSFER_DETAILS
    : ROUTES.SEND_MONEY.RECIPIENT_TYPE;

  // Deep-link / refresh past the first step / missing or superseded handover: back to where the flow branched
  useEffect(() => {
    if (!showWorkflow && !showReview) history.replace(fallbackRoute);
  }, [showWorkflow, showReview, fallbackRoute, history]);

  if (showReview && review && transactionContextId) {
    return <SendMoneyReviewScreen review={review} transactionContextId={transactionContextId} />;
  }

  if (showWorkflow && workflow) {
    return <SendMoneyWorkflowScreen workflow={workflow} />;
  }

  return null;
};

export default SendMoneyWorkflow;